// Conditional formatting rule
export function cfRule(type, params) {
  return { type, params, id: Math.random().toString(36).slice(2) };
}

// Highlight rules
export const CF_HIGHLIGHT = {
  greaterThan:  (val, threshold) => parseFloat(val) > parseFloat(threshold),
  lessThan:     (val, threshold) => parseFloat(val) < parseFloat(threshold),
  between:      (val, a, b) => { const n = parseFloat(val); return n >= parseFloat(a) && n <= parseFloat(b); },
  equalTo:      (val, q) => String(val) === String(q),
  textContains: (val, q) => String(val).toLowerCase().includes(q.toLowerCase()),
  dateOccurring:(val, period) => { const d = new Date(val); if (isNaN(d.getTime())) return false; const n = new Date(); if (period === 'today') return d.toDateString() === n.toDateString(); if (period === 'yesterday') { const y = new Date(n); y.setDate(y.getDate()-1); return d.toDateString() === y.toDateString(); } return false; },
  duplicateValues: (val, idx, allValues) => allValues.indexOf(val) !== idx,
  topN:         (val, n, allNums) => { const sorted = [...allNums].sort((a,b)=>b-a); const threshold = sorted[Math.min(n-1, sorted.length-1)]; return parseFloat(val) >= threshold; },
  bottomN:      (val, n, allNums) => { const sorted = [...allNums].sort((a,b)=>a-b); const threshold = sorted[Math.min(n-1, sorted.length-1)]; return parseFloat(val) <= threshold; },
};

export function evaluateCF(cell, rules, cellIdx, allValues, allNums) {
  for (const rule of rules) {
    if (!rule || !rule.type) continue;
    const p = rule.params || {};
    let match = false;

    if (rule.type === 'highlight') {
      const fn = CF_HIGHLIGHT[p.op];
      if (fn) {
        if (p.op === 'duplicateValues') match = fn(cell.value, cellIdx, allValues);
        else if (p.op === 'topN' || p.op === 'bottomN') match = fn(cell.value, p.n || 10, allNums);
        else if (p.op === 'between') match = fn(cell.value, p.value, p.value2);
        else if (p.op === 'dateOccurring') match = fn(cell.value, p.period);
        else match = fn(cell.value, p.value);
      }
    } else if (rule.type === 'dataBar') {
      if (!isNaN(parseFloat(cell.value))) match = true;
    } else if (rule.type === 'colorScale') {
      if (!isNaN(parseFloat(cell.value))) match = true;
    } else if (rule.type === 'iconSet') {
      if (!isNaN(parseFloat(cell.value))) match = true;
    }

    if (match) return rule;
  }
  return null;
}

export function getDataBarStyle(cell, rules, min, max) {
  const rule = rules.find(r => r.type === 'dataBar');
  if (!rule) return null;
  const val = parseFloat(cell.value);
  if (isNaN(val)) return null;
  const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
  const color = (rule.params && rule.params.color) || '#5B9BD5';
  return {
    background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, transparent ${pct}%, transparent 100%)`,
  };
}

export function getColorScaleStyle(cell, rules, min, max) {
  const rule = rules.find(r => r.type === 'colorScale');
  if (!rule) return null;
  const val = parseFloat(cell.value);
  if (isNaN(val)) return null;
  const pct = max === min ? 0.5 : (val - min) / (max - min);
  const colors = (rule.params && rule.params.colors) || ['#F8696B', '#FFEB84', '#63BE7B'];
  // Interpolate between colors
  const idx = pct < 0.5 ? 0 : 1;
  const t = pct < 0.5 ? pct * 2 : (pct - 0.5) * 2;
  const c1 = hexToRgb(colors[idx]);
  const c2 = hexToRgb(colors[Math.min(idx + 1, colors.length - 1)]);
  if (!c1 || !c2) return null;
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);
  return { backgroundColor: `rgb(${r},${g},${b})` };
}

export function getIconSetStyle(cell, rules) {
  const rule = rules.find(r => r.type === 'iconSet');
  if (!rule) return null;
  const val = parseFloat(cell.value);
  if (isNaN(val)) return null;
  const type = (rule.params && rule.params.iconType) || 'arrows';
  const icons = {
    arrows: ['⬆', '➡', '⬇'],
    flags: ['🚩', '🏁', '🏳'],
    shapes: ['●', '◑', '○'],
    rating: ['⭐⭐⭐', '⭐⭐', '⭐'],
  };
  const set = icons[type] || icons.arrows;
  const threshold1 = rule.params && rule.params.threshold1 !== undefined ? rule.params.threshold1 : 67;
  const threshold2 = rule.params && rule.params.threshold2 !== undefined ? rule.params.threshold2 : 33;
  if (val >= threshold1) return { '--cf-icon': set[0] };
  if (val >= threshold2) return { '--cf-icon': set[1] };
  return { '--cf-icon': set[2] };
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
}
